import type {
  Bill,
  IncomeEvent,
  SpendingTransaction,
} from "../api-modules/types.js";

/**
 * FIRE pure helpers — web mirror of shared/src/util/fire.ts. Web does
 * NOT import @hearth/shared (5.1 lock); the helpers live in two places
 * with the same logic. Keep them in sync.
 *
 * Phase 6 Session 1 — savings rate, FIRE numbers, time-to-FI,
 * Coast/Barista crossovers. See shared/src/util/fire.ts for the full
 * design rationale and locked positions.
 */

// === savings rate ==========================================================

export interface SavingsRateInput {
  readonly income_events: readonly IncomeEvent[];
  readonly bills: readonly Bill[];
  readonly spending_transactions: readonly SpendingTransaction[];
  readonly today: string;
  readonly lookback_months?: number;
}

export interface SavingsRateResult {
  readonly savings_rate_pct: number;
  readonly gross_income_cents: number;
  readonly net_income_cents: number;
  readonly total_outflows_cents: number;
  readonly savings_cents: number;
  readonly monthly_net_savings_cents: number;
  readonly monthly_spend_cents: number;
  readonly annualized_spend_cents: number;
}

export function savingsRate(input: SavingsRateInput): SavingsRateResult {
  const {
    income_events,
    bills,
    spending_transactions,
    today,
    lookback_months = 12,
  } = input;
  if (lookback_months < 1) {
    throw new Error(
      `savingsRate: lookback_months must be >= 1 (got ${lookback_months})`,
    );
  }
  const { startDate, endDate } = trailingWindow(today, lookback_months);

  let grossTotal = 0;
  let netTotal = 0;
  for (const evt of income_events) {
    if (evt.is_archived === true) continue;
    if (evt.date < startDate || evt.date > endDate) continue;
    grossTotal += evt.gross_cents ?? evt.amount_cents;
    netTotal += evt.net_cents ?? evt.amount_cents;
  }

  let billOutflows = 0;
  for (const bill of bills) {
    if (bill.is_archived === true) continue;
    billOutflows += billOutflowOverWindow(
      bill,
      startDate,
      endDate,
      lookback_months,
    );
  }
  let txOutflows = 0;
  for (const tx of spending_transactions) {
    if (tx.date < startDate || tx.date > endDate) continue;
    txOutflows += tx.amount_cents;
  }
  const totalOutflows = billOutflows + txOutflows;

  const savings = netTotal - totalOutflows;
  const rate = netTotal > 0 ? (savings / netTotal) * 100 : 0;

  return {
    savings_rate_pct: rate,
    gross_income_cents: grossTotal,
    net_income_cents: netTotal,
    total_outflows_cents: totalOutflows,
    savings_cents: savings,
    monthly_net_savings_cents: Math.round(savings / lookback_months),
    monthly_spend_cents: Math.round(totalOutflows / lookback_months),
    annualized_spend_cents: Math.round((totalOutflows * 12) / lookback_months),
  };
}

// === fire targets ==========================================================

export interface FireNumbersInput {
  readonly annual_spend_cents: number;
  readonly withdrawal_rate_pct?: number;
  readonly lean_spend_multiplier?: number;
  readonly fat_spend_multiplier?: number;
}

export interface FireNumbersResult {
  readonly lean_cents: number;
  readonly regular_cents: number;
  readonly fat_cents: number;
}

export function fireNumbers(input: FireNumbersInput): FireNumbersResult {
  const {
    annual_spend_cents,
    withdrawal_rate_pct = 4,
    lean_spend_multiplier = 0.7,
    fat_spend_multiplier = 2.5,
  } = input;
  if (withdrawal_rate_pct <= 0) {
    throw new Error(
      `fireNumbers: withdrawal_rate_pct must be > 0 (got ${withdrawal_rate_pct})`,
    );
  }
  const multiple = 100 / withdrawal_rate_pct;
  return {
    lean_cents: Math.round(
      annual_spend_cents * lean_spend_multiplier * multiple,
    ),
    regular_cents: Math.round(annual_spend_cents * multiple),
    fat_cents: Math.round(
      annual_spend_cents * fat_spend_multiplier * multiple,
    ),
  };
}

// === time to a generic dollar target =======================================

export interface TimeToTargetInput {
  readonly current_net_worth_cents: number;
  readonly monthly_net_savings_cents: number;
  readonly annual_return_rate_pct: number;
  readonly target_cents: number;
  readonly today: string;
  readonly max_months?: number;
}

export interface TimeToTargetResult {
  readonly months_to_target: number | null;
  readonly target_date: string | null;
  readonly already_reached: boolean;
}

export function timeToTarget(input: TimeToTargetInput): TimeToTargetResult {
  const {
    current_net_worth_cents,
    monthly_net_savings_cents,
    annual_return_rate_pct,
    target_cents,
    today,
    max_months = 1200,
  } = input;

  if (current_net_worth_cents >= target_cents) {
    return {
      months_to_target: 0,
      target_date: today,
      already_reached: true,
    };
  }

  const monthlyRate = annual_return_rate_pct / 100 / 12;
  let balance = current_net_worth_cents;
  for (let m = 1; m <= max_months; m++) {
    balance = balance * (1 + monthlyRate) + monthly_net_savings_cents;
    if (balance >= target_cents) {
      return {
        months_to_target: m,
        target_date: addMonths(today, m),
        already_reached: false,
      };
    }
    if (balance < 0 && monthly_net_savings_cents <= 0 && monthlyRate <= 0) {
      return { months_to_target: null, target_date: null, already_reached: false };
    }
  }
  return { months_to_target: null, target_date: null, already_reached: false };
}

export interface TimeToFIInput {
  readonly current_net_worth_cents: number;
  readonly monthly_net_savings_cents: number;
  readonly annual_return_rate_pct: number;
  readonly fire_target_cents: number;
  readonly today: string;
}

export interface TimeToFIResult {
  readonly months_to_fi: number | null;
  readonly years_to_fi: number | null;
  readonly fi_date: string | null;
  readonly already_fi: boolean;
}

export function timeToFI(input: TimeToFIInput): TimeToFIResult {
  const r = timeToTarget({
    current_net_worth_cents: input.current_net_worth_cents,
    monthly_net_savings_cents: input.monthly_net_savings_cents,
    annual_return_rate_pct: input.annual_return_rate_pct,
    target_cents: input.fire_target_cents,
    today: input.today,
  });
  return {
    months_to_fi: r.months_to_target,
    years_to_fi: r.months_to_target === null ? null : r.months_to_target / 12,
    fi_date: r.target_date,
    already_fi: r.already_reached,
  };
}

// === Coast FIRE ============================================================

export interface CoastFireCrossoverInput {
  readonly current_net_worth_cents: number;
  readonly monthly_net_savings_cents: number;
  readonly annual_return_rate_pct: number;
  readonly fire_target_cents: number;
  readonly current_age: number;
  readonly retirement_target_age: number;
  readonly today: string;
}

export interface CoastFireCrossoverResult {
  readonly months_to_coast: number | null;
  readonly coast_date: string | null;
  readonly already_coast: boolean;
  readonly coast_target_today_cents: number;
}

export function coastFireCrossover(
  input: CoastFireCrossoverInput,
): CoastFireCrossoverResult {
  const {
    current_net_worth_cents,
    monthly_net_savings_cents,
    annual_return_rate_pct,
    fire_target_cents,
    current_age,
    retirement_target_age,
    today,
  } = input;

  if (retirement_target_age <= current_age) {
    const alreadyCoast = current_net_worth_cents >= fire_target_cents;
    return {
      months_to_coast: alreadyCoast ? 0 : null,
      coast_date: alreadyCoast ? today : null,
      already_coast: alreadyCoast,
      coast_target_today_cents: fire_target_cents,
    };
  }

  const monthlyRate = annual_return_rate_pct / 100 / 12;
  const monthsToRetirementAt0 = (retirement_target_age - current_age) * 12;

  const compoundFullHorizon = Math.pow(
    1 + monthlyRate,
    monthsToRetirementAt0,
  );
  const coastTargetTodayCents = Math.round(
    fire_target_cents / compoundFullHorizon,
  );

  if (current_net_worth_cents >= coastTargetTodayCents) {
    return {
      months_to_coast: 0,
      coast_date: today,
      already_coast: true,
      coast_target_today_cents: coastTargetTodayCents,
    };
  }

  let balance = current_net_worth_cents;
  for (let m = 1; m <= monthsToRetirementAt0; m++) {
    balance = balance * (1 + monthlyRate) + monthly_net_savings_cents;
    const monthsRemaining = monthsToRetirementAt0 - m;
    const projectedAtRetirement =
      balance * Math.pow(1 + monthlyRate, monthsRemaining);
    if (projectedAtRetirement >= fire_target_cents) {
      return {
        months_to_coast: m,
        coast_date: addMonths(today, m),
        already_coast: false,
        coast_target_today_cents: coastTargetTodayCents,
      };
    }
  }
  return {
    months_to_coast: null,
    coast_date: null,
    already_coast: false,
    coast_target_today_cents: coastTargetTodayCents,
  };
}

// === Barista FIRE ==========================================================

export interface BaristaFireCrossoverInput {
  readonly current_net_worth_cents: number;
  readonly monthly_net_savings_cents: number;
  readonly annual_return_rate_pct: number;
  readonly annual_spend_cents: number;
  readonly barista_annual_income_cents: number;
  readonly withdrawal_rate_pct?: number;
  readonly today: string;
}

export interface BaristaFireCrossoverResult {
  readonly months_to_barista: number | null;
  readonly barista_date: string | null;
  readonly already_barista: boolean;
  readonly barista_target_cents: number;
}

export function baristaFireCrossover(
  input: BaristaFireCrossoverInput,
): BaristaFireCrossoverResult {
  const {
    current_net_worth_cents,
    monthly_net_savings_cents,
    annual_return_rate_pct,
    annual_spend_cents,
    barista_annual_income_cents,
    withdrawal_rate_pct = 4,
    today,
  } = input;

  const gap = Math.max(0, annual_spend_cents - barista_annual_income_cents);
  const baristaTargetCents = Math.round(gap * (100 / withdrawal_rate_pct));

  if (baristaTargetCents === 0) {
    return {
      months_to_barista: 0,
      barista_date: today,
      already_barista: true,
      barista_target_cents: 0,
    };
  }

  const t = timeToTarget({
    current_net_worth_cents,
    monthly_net_savings_cents,
    annual_return_rate_pct,
    target_cents: baristaTargetCents,
    today,
  });
  return {
    months_to_barista: t.months_to_target,
    barista_date: t.target_date,
    already_barista: t.already_reached,
    barista_target_cents: baristaTargetCents,
  };
}

// === internal helpers ======================================================

function trailingWindow(
  today: string,
  lookback_months: number,
): { startDate: string; endDate: string } {
  const [yStr, mStr] = today.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const endTotal0 = y * 12 + (m - 1) - 1;
  const startTotal0 = endTotal0 - (lookback_months - 1);
  const endY = Math.floor(endTotal0 / 12);
  const endM0 = endTotal0 % 12;
  const startY = Math.floor(startTotal0 / 12);
  const startM0 = startTotal0 % 12;
  const lastDayOfEnd = new Date(Date.UTC(endY, endM0 + 1, 0)).getUTCDate();
  return {
    startDate: `${pad4(startY)}-${pad2(startM0 + 1)}-01`,
    endDate: `${pad4(endY)}-${pad2(endM0 + 1)}-${pad2(lastDayOfEnd)}`,
  };
}

function billOutflowOverWindow(
  bill: Bill,
  startDate: string,
  endDate: string,
  lookback_months: number,
): number {
  if (bill.recurrence === "one_time") {
    if (bill.due_date >= startDate && bill.due_date <= endDate) {
      return bill.amount_cents;
    }
    return 0;
  }
  let cyclesPerMonth: number;
  switch (bill.recurrence) {
    case "monthly":
      cyclesPerMonth = 1;
      break;
    case "quarterly":
      cyclesPerMonth = 1 / 3;
      break;
    case "annual":
      cyclesPerMonth = 1 / 12;
      break;
    default: {
      const _exhaustive: never = bill.recurrence;
      void _exhaustive;
      return 0;
    }
  }
  return Math.round(bill.amount_cents * cyclesPerMonth * lookback_months);
}

function addMonths(today: string, months: number): string {
  const [yStr, mStr, dStr] = today.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const total0 = y * 12 + (m - 1) + months;
  const newY = Math.floor(total0 / 12);
  const newM0 = total0 % 12;
  const lastDay = new Date(Date.UTC(newY, newM0 + 1, 0)).getUTCDate();
  const newD = Math.min(d, lastDay);
  return `${pad4(newY)}-${pad2(newM0 + 1)}-${pad2(newD)}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
function pad4(n: number): string {
  return n.toString().padStart(4, "0");
}
